import type { ItemFieldRow } from './item-field-reader';
import { phpJsonInteger } from './php-integer-arithmetic';
import { phpJsonString, phpJsonTruthy } from './php-json-values';

export interface StatusStackCatalog {
    rows: readonly { readonly item_id: string; readonly variable_name: string }[];
    quantityGroups: Readonly<Record<string, { readonly aliases?: Readonly<Record<string, number>> }>>;
    stackedGroups: Readonly<Record<string, { readonly levels?: readonly string[] }>>;
}
function arrayLike(value: unknown): value is ItemFieldRow { return value !== null && typeof value === 'object'; }
function own(value: unknown, key: string): unknown {
    return arrayLike(value) && Object.prototype.hasOwnProperty.call(value, key) ? (value as Readonly<Record<string, unknown>>)[key] : undefined;
}
/** StatusEffect::statusRowStack: one cell collapse, no quantity cap or alias-level substitution. */
export function readStatusRowStack(row: ItemFieldRow): number {
    return Number(statusRowStackInteger(row));
}
/** Internal composed arithmetic keeps PHP's integer before JSON projection. */
export function statusRowStackInteger(row: ItemFieldRow): bigint {
    if (!arrayLike(row)) throw new TypeError('Status row must be a PHP array');
    const value = own(row, 'status_stack') ?? 1;
    const integer = phpJsonInteger(arrayLike(value) ? own(value, '0') ?? 1 : value);
    return integer < 1n ? 1n : integer;
}
/** Read-only StatusEffect::getCurrentStackLevel. This does not migrate or write
 * either status model. First matching row wins; exact catalog UUID case matters.
 */
export function createStatusStackReader(catalog: StatusStackCatalog, deps: {
    readStatuses: (row: ItemFieldRow) => ItemFieldRow;
    readItemField: (row: ItemFieldRow | null, key: string) => unknown;
}): { read: (row: ItemFieldRow, baseId: string) => number | null; id: (name: string) => string | null } {
    const byId = new Map<string, string>(), byName = new Map<string, string>();
    for (const entry of catalog.rows) {
        if (!byId.has(entry.item_id)) byId.set(entry.item_id, entry.variable_name);
        if (!byName.has(entry.variable_name)) byName.set(entry.variable_name, entry.item_id);
    }
    function quantityFamily(id: string): string | null {
        const name = byId.get(id);
        if (name === undefined) return null;
        for (const [base, group] of Object.entries(catalog.quantityGroups)) {
            if (name === base || own(group.aliases, name) != null) return phpJsonTruthy(byName.get(base)) ? base : null;
        }
        return null;
    }
    function stackInfo(id: unknown): { base: string; level: number } | null {
        if (arrayLike(id)) throw new TypeError('Status catalog ID must be scalar');
        const name = byId.get(phpJsonString(id));
        if (name === undefined) return null;
        if (own(catalog.stackedGroups, name) != null) return { base: name, level: 1 };
        for (const [base, group] of Object.entries(catalog.stackedGroups)) {
            const index = (group.levels ?? []).indexOf(name);
            if (index !== -1) return { base, level: index + 1 };
        }
        return null;
    }
    return { id: name => byName.get(name) ?? null, read: (row, baseId) => {
        if (!phpJsonTruthy(deps.readStatuses(row))) return null;
        const baseName = byId.get(baseId);
        const quantity = baseName !== undefined && own(catalog.quantityGroups, baseName) != null;
        if (!quantity && (baseName === undefined || own(catalog.stackedGroups, baseName) == null)) return null;
        for (const status of Object.values(deps.readStatuses(row))) {
            if (status !== null && !arrayLike(status)) throw new TypeError('Status item_field row must be a PHP array or null');
            const field = deps.readItemField(status, 'item_id');
            if (quantity) {
                const id = field ?? own(status, 'status_item_id') ?? '';
                if (typeof id !== 'string' || id === '') continue;
                if (quantityFamily(id) === baseName) return readStatusRowStack(status as ItemFieldRow);
            } else {
                const id = field ?? '';
                if (id === '') continue;
                const info = stackInfo(id);
                if (info !== null && info.base === baseName) return info.level;
            }
        }
        return null;
    } };
}
